/**
 * Process image using pixel manipulation instead of CSS filters
 * More reliable across different WebView implementations
 */

import { removeBackground as removeBackgroundUtil, softenEdges } from './colorUtils';

/**
 * Invert image colors at pixel level
 */
function invertPixels(imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i];         // Red
    data[i + 1] = 255 - data[i + 1]; // Green
    data[i + 2] = 255 - data[i + 2]; // Blue
    // Alpha (data[i + 3]) stays the same
  }
  return imageData;
}

/**
 * Convert to grayscale at pixel level
 */
function grayscalePixels(imageData) {
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    // Use luminosity method for better results
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    data[i] = gray;     // Red
    data[i + 1] = gray; // Green
    data[i + 2] = gray; // Blue
    // Alpha stays the same
  }
  return imageData;
}

/**
 * Adjust brightness at pixel level
 */
function adjustBrightness(imageData, brightness) {
  const factor = brightness / 100;
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.min(255, data[i] * factor);         // Red
    data[i + 1] = Math.min(255, data[i + 1] * factor); // Green
    data[i + 2] = Math.min(255, data[i + 2] * factor); // Blue
  }
  return imageData;
}

/**
 * Adjust contrast at pixel level
 */
function adjustContrast(imageData, contrast) {
  const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.min(255, Math.max(0, factor * (data[i] - 128) + 128));
    data[i + 1] = Math.min(255, Math.max(0, factor * (data[i + 1] - 128) + 128));
    data[i + 2] = Math.min(255, Math.max(0, factor * (data[i + 2] - 128) + 128));
  }
  return imageData;
}

/**
 * Process image using pixel manipulation (fallback for when CSS filters don't work)
 */
export async function processImageWithPixels(imageDataUrl, filters) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      try {
        console.log('Processing with pixel manipulation:', filters);
        console.log('Original image size:', img.width, 'x', img.height);

        // Optimize: limit maximum processing size to reduce sluggishness
        const MAX_SIZE = 2000; // pixels
        let width = img.width;
        let height = img.height;

        // Scale down if too large
        if (width > MAX_SIZE || height > MAX_SIZE) {
          const scale = Math.min(MAX_SIZE / width, MAX_SIZE / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          console.log('Downscaling for performance to:', width, 'x', height);
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw image (scaled if necessary)
        ctx.drawImage(img, 0, 0, width, height);

        // Get pixel data
        let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Apply filters in order
        if (filters.invert) {
          console.log('Applying pixel invert');
          imageData = invertPixels(imageData);
        }

        if (filters.grayscale) {
          console.log('Applying pixel grayscale');
          imageData = grayscalePixels(imageData);
        }

        if (filters.brightness !== 100) {
          console.log('Applying pixel brightness:', filters.brightness);
          imageData = adjustBrightness(imageData, filters.brightness);
        }

        if (filters.contrast !== 100) {
          console.log('Applying pixel contrast:', filters.contrast);
          imageData = adjustContrast(imageData, filters.contrast);
        }

        // Apply background removal if enabled
        if (filters.removeBackground) {
          console.log('Applying background removal with tolerance:', filters.bgRemovalTolerance || 0.15);
          imageData = removeBackgroundUtil(imageData, filters.bgRemovalTolerance || 0.15);

          // Apply edge softening if enabled
          if (filters.bgSoftenEdges) {
            console.log('Applying edge softening');
            imageData = softenEdges(imageData, 1);
          }
        }

        // Put modified pixels back
        ctx.putImageData(imageData, 0, 0);

        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/png');
        console.log('Pixel processing complete, size:', dataUrl.length);

        resolve(dataUrl);
      } catch (error) {
        console.error('Error in pixel processing:', error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error('Error loading image for pixel processing:', error);
      reject(new Error('Failed to load image'));
    };

    if (!imageDataUrl.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }

    img.src = imageDataUrl;
  });
}
